import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Calendar as BigCalendar, dateFnsLocalizer } from 'react-big-calendar';
import { useNavigate } from 'react-router-dom';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import enUS from 'date-fns/locale/en-US';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './Calendar.css';
import './Dashboard.css';

import Navbar from '../components/profile-components/Navbar';
import Topbar from '../components/profile-components/Topbar';
import TaskModal from '../components/Tasks/TaskModal';
import CreateTaskModal from '../components/Tasks/CreateTaskModal';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'https://agent-book-crm-backend.onrender.com';

const DEFAULT_TIMEZONE = 'America/Chicago';
const CALENDAR_SETTINGS_KEY = 'agentBookCalendarSettings';

const DEFAULT_CALENDAR_SETTINGS = {
  showHolidays: true,
  showCompletedTasks: true,
  taskColor: '#2563eb',
  todayTaskColor: '#d97706',
  overdueTaskColor: '#dc2626',
  completedTaskColor: '#6b7280',
  holidayColor: '#7c3aed',
};

const locales = { 'en-US': enUS };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

function getBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
}

function isValidTimezone(timezone) {
  if (!timezone || typeof timezone !== 'string') return false;

  try {
    Intl.DateTimeFormat('en-US', { timeZone: timezone }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

function cleanDatePart(rawDate) {
  if (!rawDate) return '';

  if (rawDate instanceof Date) {
    const year = rawDate.getFullYear();
    const month = String(rawDate.getMonth() + 1).padStart(2, '0');
    const day = String(rawDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  return String(rawDate).split('T')[0].split(' ')[0];
}

function parseAccountDate(rawDate) {
  const datePart = cleanDatePart(rawDate);
  if (!datePart) return null;

  const [year, month, day] = datePart.split('-').map(Number);

  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day);
}

function parseTimeParts(rawTime, fallback = '00:00:00') {
  const time = rawTime || fallback;
  const [hour = '0', minute = '0'] = String(time).split(':');

  return {
    hour: Number(hour) || 0,
    minute: Number(minute) || 0,
  };
}

function buildAccountDateTime(rawDate, rawTime) {
  const baseDate = parseAccountDate(rawDate);
  if (!baseDate) return null;

  const { hour, minute } = parseTimeParts(rawTime);

  baseDate.setHours(hour, minute, 0, 0);

  return baseDate;
}

function formatDateInput(date) {
  if (!date) return '';
  return format(date, 'yyyy-MM-dd');
}

function formatTimeInput(date) {
  if (!date) return '';
  return format(date, 'HH:mm');
}

function formatTime12(rawTime) {
  if (!rawTime) return '';

  const { hour, minute } = parseTimeParts(rawTime);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  const displayMinute = String(minute).padStart(2, '0');

  return `${displayHour}:${displayMinute} ${suffix}`;
}

function formatDateDisplay(rawDate) {
  const date = parseAccountDate(rawDate);
  if (!date) return 'No date';

  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getReadableTextColor(hexColor) {
  if (!hexColor || !/^#[0-9a-f]{6}$/i.test(hexColor)) {
    return '#ffffff';
  }

  const red = parseInt(hexColor.slice(1, 3), 16);
  const green = parseInt(hexColor.slice(3, 5), 16);
  const blue = parseInt(hexColor.slice(5, 7), 16);
  const brightness = (red * 299 + green * 587 + blue * 114) / 1000;

  return brightness > 160 ? '#0f172a' : '#ffffff';
}

export default function Calendar() {
  const navigate = useNavigate();

  const [events, setEvents] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCalendarSettings, setShowCalendarSettings] = useState(false);

  const [createDefaults, setCreateDefaults] = useState({
    initialDate: '',
    initialStartTime: '',
    initialEndTime: '',
  });

  const [loading, setLoading] = useState(true);
  const [loadingCalendarFeed, setLoadingCalendarFeed] = useState(false);
  const [regeneratingCalendarFeed, setRegeneratingCalendarFeed] = useState(false);
  const [calendarFeed, setCalendarFeed] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [accountTimezone, setAccountTimezone] = useState(
    getBrowserTimezone() || DEFAULT_TIMEZONE
  );

  const [calendarSettings, setCalendarSettings] = useState(() => {
    try {
      const saved = localStorage.getItem(CALENDAR_SETTINGS_KEY);

      return saved
        ? { ...DEFAULT_CALENDAR_SETTINGS, ...JSON.parse(saved) }
        : DEFAULT_CALENDAR_SETTINGS;
    } catch {
      return DEFAULT_CALENDAR_SETTINGS;
    }
  });

  const [filters, setFilters] = useState({
    status: '',
    type: '',
    recordType: '',
  });

  const currentYear = useMemo(() => currentDate.getFullYear(), [currentDate]);

  useEffect(() => {
    localStorage.setItem(CALENDAR_SETTINGS_KEY, JSON.stringify(calendarSettings));
  }, [calendarSettings]);

  const updateCalendarSetting = (key, value) => {
    setCalendarSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const resetCalendarSettings = () => {
    setCalendarSettings(DEFAULT_CALENDAR_SETTINGS);
  };

  const getTaskRecordType = (task) => {
    if (task?.lead_id) return 'Lead';
    if (task?.client_id) return 'Client';
    return 'Unknown';
  };

  const getTaskProfilePath = (task) => {
    if (task?.lead_id) return `/lead-profile/${task.lead_id}`;
    if (task?.client_id) return `/client-profile/${task.client_id}`;
    return null;
  };

  const buildTaskEvent = (task) => {
    const start = buildAccountDateTime(task.due_date, task.start_time || '00:00:00');
    if (!start) return null;

    const end =
      buildAccountDateTime(task.due_date, task.end_time || task.start_time || '00:30:00') ||
      new Date(start.getTime() + 30 * 60 * 1000);

    if (end <= start) {
      end.setTime(start.getTime() + 30 * 60 * 1000);
    }

    const name = task.full_name || task.client_name || task.lead_name || 'Unknown';
    const type = task.type || 'Task';

    return {
      id: `task-${task.id}`,
      type: 'task',
      task,
      title: `${name} — ${type} — ${task.title}`,
      start,
      end,
      allDay: false,
    };
  };

  const loadCalendarFeed = useCallback(async () => {
    try {
      setLoadingCalendarFeed(true);

      const token = localStorage.getItem('token');
      if (!token) return;

      const res = await fetch(`${API_BASE_URL}/api/calendar-feed/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-AgentBook-Public-Origin': window.location.origin,
        },
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.error || data.message || 'Failed to load calendar feed.');
      }

      setCalendarFeed(data.calendarFeed || null);
    } catch (err) {
      console.error('❌ Failed to load calendar feed:', err.message);
    } finally {
      setLoadingCalendarFeed(false);
    }
  }, []);

  const loadUserTimezone = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return getBrowserTimezone() || DEFAULT_TIMEZONE;

      const res = await fetch(`${API_BASE_URL}/api/users/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        return getBrowserTimezone() || DEFAULT_TIMEZONE;
      }

      const data = await res.json();
      const savedTimezone = data?.timezone;

      if (isValidTimezone(savedTimezone)) {
        setAccountTimezone(savedTimezone);
        return savedTimezone;
      }

      const browserTimezone = getBrowserTimezone();

      if (isValidTimezone(browserTimezone)) {
        setAccountTimezone(browserTimezone);
        return browserTimezone;
      }

      setAccountTimezone(DEFAULT_TIMEZONE);
      return DEFAULT_TIMEZONE;
    } catch (err) {
      console.error('❌ Failed to load account timezone:', err.message);
      return getBrowserTimezone() || DEFAULT_TIMEZONE;
    }
  }, []);

  const loadCalendarData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      await loadUserTimezone();

      const [resUpcomingTasks, resOverdueTasks, holidayRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/tasks/upcoming?days=90`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_BASE_URL}/api/tasks/overdue`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`https://date.nager.at/api/v3/PublicHolidays/${currentYear}/US`),
      ]);

      if (!resUpcomingTasks.ok) throw new Error('Failed to load upcoming tasks');
      if (!resOverdueTasks.ok) throw new Error('Failed to load overdue tasks');

      const upcomingTasksData = await resUpcomingTasks.json();
      const overdueTasksData = await resOverdueTasks.json();

      const taskMap = new Map();

      [...overdueTasksData, ...upcomingTasksData].forEach((task) => {
        taskMap.set(Number(task.id), task);
      });

      const tasksData = Array.from(taskMap.values());
      const holidays = holidayRes.ok ? await holidayRes.json() : [];

      const taskEvents = tasksData.map(buildTaskEvent).filter(Boolean);

      const holidayEvents = holidays.map((h) => {
        const [year, month, day] = h.date.split('-').map(Number);
        const d = new Date(year, month - 1, day);

        return {
          id: `holiday-${h.date}`,
          type: 'holiday',
          title: h.localName,
          allDay: true,
          start: d,
          end: d,
        };
      });

      setTasks(tasksData);
      setEvents([...taskEvents, ...holidayEvents]);
    } catch (err) {
      console.error('❌ Calendar load failed:', err.message);
    } finally {
      setLoading(false);
    }
  }, [currentYear, loadUserTimezone]);

  useEffect(() => {
    loadCalendarData();
  }, [loadCalendarData]);

  useEffect(() => {
    loadCalendarFeed();
  }, [loadCalendarFeed]);

  const uniqueStatuses = useMemo(
    () => Array.from(new Set(tasks.map((task) => task.status).filter(Boolean))).sort(),
    [tasks]
  );

  const uniqueTaskTypes = useMemo(
    () => Array.from(new Set(tasks.map((task) => task.type).filter(Boolean))).sort(),
    [tasks]
  );

  const filteredTasks = useMemo(() => {
    return tasks.filter((task) => {
      const matchesStatus = !filters.status || (task.status || '') === filters.status;
      const matchesType = !filters.type || (task.type || '') === filters.type;
      const matchesRecordType =
        !filters.recordType || getTaskRecordType(task) === filters.recordType;
      const matchesCompletedVisibility =
        calendarSettings.showCompletedTasks || task.status !== 'Completed';

      return (
        matchesStatus &&
        matchesType &&
        matchesRecordType &&
        matchesCompletedVisibility
      );
    });
  }, [tasks, filters, calendarSettings.showCompletedTasks]);

  const filteredTaskIds = useMemo(
    () => new Set(filteredTasks.map((task) => `task-${task.id}`)),
    [filteredTasks]
  );

  const filteredEvents = useMemo(() => {
    return events.filter((event) => {
      if (event.type === 'holiday') return calendarSettings.showHolidays;
      return filteredTaskIds.has(event.id);
    });
  }, [events, filteredTaskIds, calendarSettings.showHolidays]);

  const todayTasks = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return filteredTasks.filter((task) => {
      const taskDate = parseAccountDate(task.due_date);
      if (!taskDate) return false;
      taskDate.setHours(0, 0, 0, 0);
      return taskDate.getTime() === today.getTime();
    });
  }, [filteredTasks]);

  const overdueTasks = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return filteredTasks.filter((task) => {
      const taskDate = parseAccountDate(task.due_date);
      if (!taskDate) return false;
      taskDate.setHours(0, 0, 0, 0);
      return taskDate.getTime() < today.getTime() && task.status !== 'Completed';
    });
  }, [filteredTasks]);

  const upcomingTasks = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const upcomingWindowEnd = new Date(today);
    upcomingWindowEnd.setDate(upcomingWindowEnd.getDate() + 7);

    return filteredTasks.filter((task) => {
      const taskDate = parseAccountDate(task.due_date);
      if (!taskDate) return false;
      taskDate.setHours(0, 0, 0, 0);
      return (
        taskDate.getTime() > today.getTime() &&
        taskDate.getTime() <= upcomingWindowEnd.getTime()
      );
    });
  }, [filteredTasks]);

  const handleSelectEvent = (event) => {
    if (event.type === 'task') {
      setSelectedTask(event.task);
      setShowTaskModal(true);
    }
  };

  const handleSelectSlot = ({ start, end, action }) => {
    if (!start) return;

    const initialDate = formatDateInput(start);

    const isAllDayLike =
      action === 'click' ||
      (start.getHours() === 0 &&
        start.getMinutes() === 0 &&
        end &&
        end.getHours() === 0 &&
        end.getMinutes() === 0);

    let initialStartTime = '';
    let initialEndTime = '';

    if (!isAllDayLike) {
      initialStartTime = formatTimeInput(start);
      initialEndTime = end ? formatTimeInput(end) : '';
    }

    setCreateDefaults({
      initialDate,
      initialStartTime,
      initialEndTime,
    });
    setShowCreateModal(true);
  };

  const closeTaskModal = () => {
    setShowTaskModal(false);
    setSelectedTask(null);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setCreateDefaults({
      initialDate: '',
      initialStartTime: '',
      initialEndTime: '',
    });
  };

  const handleTaskUpdated = async () => {
    closeTaskModal();
    await loadCalendarData();
  };

  const handleTaskCreated = async () => {
    closeCreateModal();
    await loadCalendarData();
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      type: '',
      recordType: '',
    });
  };

  const handleQuickComplete = async (task, e) => {
    e.stopPropagation();

    try {
      const res = await fetch(`${API_BASE_URL}/api/tasks/${task.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          status: 'Completed',
          dueDate: cleanDatePart(task.due_date) || null,
          startTime: task.start_time || null,
          endTime: task.end_time || null,
          note: task.note || '',
        }),
      });

      if (!res.ok) {
        throw new Error('Failed to complete task');
      }

      await loadCalendarData();
    } catch (err) {
      console.error('❌ Quick complete failed:', err);
      alert('Unable to complete task.');
    }
  };

  const handleOpenProfile = (task, e) => {
    e.stopPropagation();
    const path = getTaskProfilePath(task);
    if (path) navigate(path);
  };

  const handleCopyCalendarFeed = async () => {
    if (!calendarFeed?.feedUrl) {
      alert('Calendar subscription link is not available yet.');
      return;
    }

    try {
      await navigator.clipboard.writeText(calendarFeed.feedUrl);
      alert('Calendar subscription link copied.');
    } catch {
      alert('Copy failed. Please highlight and copy the link manually.');
    }
  };

  const handleRegenerateCalendarFeed = async () => {
    if (
      !window.confirm(
        'Regenerate your calendar subscription link? Your old calendar link will stop working.'
      )
    ) {
      return;
    }

    try {
      setRegeneratingCalendarFeed(true);

      const token = localStorage.getItem('token');

      const res = await fetch(`${API_BASE_URL}/api/calendar-feed/regenerate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'X-AgentBook-Public-Origin': window.location.origin,
        },
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.error || data.message || 'Failed to regenerate calendar link.');
      }

      setCalendarFeed(data.calendarFeed || null);
      alert('Calendar subscription link regenerated.');
    } catch (err) {
      console.error('❌ Failed to regenerate calendar feed:', err.message);
      alert(err.message || 'Failed to regenerate calendar link.');
    } finally {
      setRegeneratingCalendarFeed(false);
    }
  };

  const formatTaskTime = (task) => {
    return formatTime12(task.start_time || '00:00:00');
  };

  const formatTaskDate = (task) => {
    return formatDateDisplay(task.due_date);
  };

  const eventPropGetter = (event) => {
  let color = calendarSettings.taskColor;
  let className = 'calendar-event-task';

  if (event.type === 'holiday') {
    color = calendarSettings.holidayColor;
    className = 'calendar-event-holiday';
  } else {
    const task = event.task;
    const dueDate = parseAccountDate(task?.due_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (task?.status === 'Completed') {
      color = calendarSettings.completedTaskColor;
      className = 'calendar-event-completed';
    } else if (dueDate) {
      dueDate.setHours(0, 0, 0, 0);

      if (dueDate < today) {
        color = calendarSettings.overdueTaskColor;
        className = 'calendar-event-overdue';
      } else if (dueDate.getTime() === today.getTime()) {
        color = calendarSettings.todayTaskColor;
        className = 'calendar-event-today';
      }
    }
  }

  return {
    className,
    style: {
      backgroundColor: color,
      background: color,
      borderColor: color,
      color: getReadableTextColor(color),
    },
  };
};

  const TaskList = ({ title, items, emptyText }) => (
    <div className="calendar-rail-section">
      <div className="calendar-rail-section-head">
        <h3>{title}</h3>
        <span>{items.length}</span>
      </div>

      {items.length ? (
        <div className="calendar-task-list">
          {items.map((task) => {
            const profilePath = getTaskProfilePath(task);

            return (
              <button
                key={task.id}
                type="button"
                className="calendar-task-card"
                onClick={() => {
                  setSelectedTask(task);
                  setShowTaskModal(true);
                }}
              >
                <div className="calendar-task-time">
                  {formatTaskDate(task)} · {formatTaskTime(task)}
                </div>
                <div className="calendar-task-title">{task.title}</div>
                <div className="calendar-task-meta">
                  {task.full_name || 'Unknown'} · {task.type || 'Task'} · {getTaskRecordType(task)}
                </div>

                <div className="calendar-task-actions">
                  {profilePath && (
                    <button
                      type="button"
                      className="calendar-task-action-btn"
                      onClick={(e) => handleOpenProfile(task, e)}
                    >
                      Open Profile
                    </button>
                  )}

                  {task.status !== 'Completed' && (
                    <button
                      type="button"
                      className="calendar-task-action-btn calendar-task-action-btn-primary"
                      onClick={(e) => handleQuickComplete(task, e)}
                    >
                      Mark Complete
                    </button>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <p className="calendar-empty-text">{emptyText}</p>
      )}
    </div>
  );

  const CalendarColorSetting = ({ label, settingKey }) => (
    <label className="dashboard-setting-row calendar-color-setting-row">
      <span>{label}</span>
      <input
        type="color"
        value={calendarSettings[settingKey]}
        onChange={(event) => updateCalendarSetting(settingKey, event.target.value)}
      />
    </label>
  );

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page dashboard-modern-page">
        <div className="sales-hero sales-profile-hero dashboard-hero-modern">
          <div>
            <p className="sales-eyebrow">Agent Book CRM</p>
            <h1 className="sales-title">Calendar</h1>
            <p className="sales-subtitle">
              View your schedule, manage task timing, and stay on top of today’s and upcoming work.
            </p>
            <p className="sales-subtitle" style={{ marginTop: '6px' }}>
              Calendar timezone: <strong>{accountTimezone}</strong>
            </p>
          </div>

          <button
            type="button"
            className="dashboard-settings-btn"
            onClick={() => setShowCalendarSettings(true)}
            aria-label="Calendar settings"
            title="Calendar settings"
          >
            ⚙
          </button>
        </div>

        <div className="sales-toolbar-card">
          <div
            style={{
              display: 'flex',
              gap: '10px',
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <select
              value={filters.status}
              onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
              className="sales-search-input"
              style={{ maxWidth: '220px' }}
            >
              <option value="">All Statuses</option>
              {uniqueStatuses.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>

            <select
              value={filters.type}
              onChange={(e) => setFilters((prev) => ({ ...prev, type: e.target.value }))}
              className="sales-search-input"
              style={{ maxWidth: '220px' }}
            >
              <option value="">All Task Types</option>
              {uniqueTaskTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>

            <select
              value={filters.recordType}
              onChange={(e) => setFilters((prev) => ({ ...prev, recordType: e.target.value }))}
              className="sales-search-input"
              style={{ maxWidth: '200px' }}
            >
              <option value="">All Record Types</option>
              <option value="Lead">Lead</option>
              <option value="Client">Client</option>
              <option value="Unknown">Unknown</option>
            </select>

            <button
              onClick={clearFilters}
              className="sales-secondary-btn"
              type="button"
            >
              Clear Filters
            </button>
          </div>
        </div>

        <div className="calendar-layout">
          <div className="calendar-main sales-table-card">
            <div className="sales-table-header">
              <div>
                <h2>Schedule</h2>
                <p>
                  {loading
                    ? 'Loading events...'
                    : `${filteredEvents.length} visible calendar item${filteredEvents.length === 1 ? '' : 's'}`}
                </p>
              </div>
            </div>

            <div className="calendar-shell">
              <BigCalendar
                localizer={localizer}
                events={filteredEvents}
                defaultView="month"
                views={['month', 'week', 'day']}
                date={currentDate}
                onNavigate={(date) => setCurrentDate(date)}
                startAccessor="start"
                endAccessor="end"
                onSelectEvent={handleSelectEvent}
                onSelectSlot={handleSelectSlot}
                selectable
                eventPropGetter={eventPropGetter}
                popup
                style={{ height: 680 }}
              />
            </div>
          </div>

          <div className="calendar-sidebar">
            <div className="calendar-rail sales-section-card sales-section-lead">
              <div className="sales-section-head">
                <h2>Task Queue</h2>
                <p>
                  Keep your day organized. Times are shown in {accountTimezone}.
                </p>
              </div>

              <TaskList
                title="Today"
                items={todayTasks}
                emptyText="No tasks scheduled for today."
              />

              <TaskList
                title="Overdue"
                items={overdueTasks}
                emptyText="No overdue tasks."
              />

              <TaskList
                title="Upcoming (7 Days)"
                items={upcomingTasks}
                emptyText="No upcoming tasks in the next 7 days."
              />
            </div>
          </div>
        </div>
      </div>

      {showCalendarSettings && (
        <div
          className="dashboard-settings-overlay"
          onClick={() => setShowCalendarSettings(false)}
        >
          <div
            className="dashboard-settings-panel calendar-settings-compact"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="dashboard-settings-header">
              <div>
                <p>Calendar</p>
                <h2>Settings</h2>
              </div>

              <button type="button" onClick={() => setShowCalendarSettings(false)}>
                ×
              </button>
            </div>

            <div className="dashboard-settings-body">
              <div className="calendar-settings-section">
                <h3>Calendar Subscription</h3>
                <p>
                  Subscribe to your Agent Book CRM appointments in Google Calendar,
                  Outlook, or Apple Calendar. This is one-way only.
                </p>

                <input
                  className="sales-search-input"
                  value={
                    loadingCalendarFeed
                      ? 'Loading calendar subscription link...'
                      : calendarFeed?.feedUrl || ''
                  }
                  readOnly
                  placeholder="Calendar subscription link unavailable"
                />

                <div className="calendar-settings-actions">
                  <button
                    type="button"
                    className="sales-primary-btn"
                    onClick={handleCopyCalendarFeed}
                    disabled={loadingCalendarFeed || !calendarFeed?.feedUrl}
                  >
                    Copy Calendar Link
                  </button>

                  <button
                    type="button"
                    className="sales-secondary-btn"
                    onClick={handleRegenerateCalendarFeed}
                    disabled={loadingCalendarFeed || regeneratingCalendarFeed}
                  >
                    {regeneratingCalendarFeed ? 'Regenerating...' : 'Regenerate Link'}
                  </button>
                </div>

                <p className="calendar-settings-note">
                  Paste this link into Google Calendar or Outlook as a subscribed calendar.
                  Do not share it publicly.
                </p>
              </div>

              <div className="calendar-settings-section">
                <h3>Display Options</h3>

                <label className="dashboard-setting-row">
                  <span>Show holidays</span>
                  <input
                    type="checkbox"
                    checked={calendarSettings.showHolidays}
                    onChange={() =>
                      updateCalendarSetting('showHolidays', !calendarSettings.showHolidays)
                    }
                  />
                </label>

                <label className="dashboard-setting-row">
                  <span>Show completed tasks</span>
                  <input
                    type="checkbox"
                    checked={calendarSettings.showCompletedTasks}
                    onChange={() =>
                      updateCalendarSetting(
                        'showCompletedTasks',
                        !calendarSettings.showCompletedTasks
                      )
                    }
                  />
                </label>
              </div>

              <div className="calendar-settings-section">
                <h3>Calendar Colors</h3>

                <CalendarColorSetting label="Regular tasks" settingKey="taskColor" />
                <CalendarColorSetting label="Today's tasks" settingKey="todayTaskColor" />
                <CalendarColorSetting label="Overdue tasks" settingKey="overdueTaskColor" />
                <CalendarColorSetting label="Completed tasks" settingKey="completedTaskColor" />
                <CalendarColorSetting label="Holidays" settingKey="holidayColor" />
              </div>
            </div>

            <div className="dashboard-settings-footer">
              <button type="button" className="sales-secondary-btn" onClick={resetCalendarSettings}>
                Reset
              </button>

              <button
                type="button"
                className="sales-primary-btn"
                onClick={() => setShowCalendarSettings(false)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {showTaskModal && selectedTask && (
        <TaskModal
          task={selectedTask}
          onClose={closeTaskModal}
          onUpdate={handleTaskUpdated}
          source="calendar"
        />
      )}

      {showCreateModal && (
        <CreateTaskModal
          onClose={closeCreateModal}
          onCreated={handleTaskCreated}
          relatedId={null}
          relatedType={null}
          initialDate={createDefaults.initialDate}
          initialStartTime={createDefaults.initialStartTime}
          initialEndTime={createDefaults.initialEndTime}
        />
      )}
    </>
  );
}